import { AnalyticIdentifier } from '@common/neo.tax';
import {
  Button,
  ModalActions,
  ModalHeader,
  Stack,
  Typography,
  getRecipeColor,
  InputFormContainer,
  ControlledTextArea,
} from '@ui-components/neo.tax';
import { ReactComponent as LabTube } from '@ui-components/neo.tax/dist/icons/lab-tube-bottle.svg';
import { useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import { useUpdateRdProject, RdProject } from '@neo.tax/rd/hooks';
import { useAnalyticId, ProjectEligibilityModal, HIGHLIGHT_VARIANT } from '@neo.tax/shared';

interface RdProjectProcessOfExperimentationModalProps {
  isOpen: boolean;
  onClose: () => void;
  rdProject: RdProject;
}

interface RdProjectProcessOfExperimentationFormProps {
  processOfExperimentation: string;
}

export const RdProjectProcessOfExperimentationModal = ({
  isOpen,
  onClose,
  rdProject,
}: RdProjectProcessOfExperimentationModalProps): React.ReactElement => {
  const [updateRdProject] = useUpdateRdProject({ rdProject, includeOptimisticResponse: true });
  const { control, handleSubmit, reset } = useForm<RdProjectProcessOfExperimentationFormProps>({
    mode: 'onSubmit',
    defaultValues: {
      processOfExperimentation: rdProject.processOfExperimentation ?? '',
    },
  });

  const processOfExperimentationId = useAnalyticId(AnalyticIdentifier.ENTERPRISE_PROJECT_PROCESS_OF_EXPERIMENTATION);

  const handleCancel = (): void => {
    reset();
    onClose();
  };

  const onSubmit: SubmitHandler<RdProjectProcessOfExperimentationFormProps> = async (data) => {
    await updateRdProject(data);
    onClose();
  };

  useEffect(() => {
    if (isOpen) {
      reset({
        processOfExperimentation: rdProject.processOfExperimentation ?? '',
      });
    }
  }, [isOpen, rdProject.processOfExperimentation, reset]);

  return (
    <ProjectEligibilityModal isOpen={isOpen}>
      <Stack as="form" direction="column" gap={8} onSubmit={handleSubmit(onSubmit)} paddingX={4}>
        <ModalHeader
          icon={<LabTube width={24} color={getRecipeColor(HIGHLIGHT_VARIANT, 'colorBg').text} />}
          iconBgColor={getRecipeColor(HIGHLIGHT_VARIANT, 'colorBg').bg}
          title={
            <Typography fontSize="base" fontWeight="semibold">
              Project{' '}
              <Typography
                fontSize="inherit"
                fontWeight="inherit"
                color={getRecipeColor(HIGHLIGHT_VARIANT, 'whiteBg').text}
              >
                {rdProject.name}
              </Typography>{' '}
              Process Of Experimentation
            </Typography>
          }
          copy="Describe why project meets the R&D requirements"
        />

        <InputFormContainer
          label="Please describe the process of experimentation in 1-2 paragraphs"
          inputId={processOfExperimentationId}
        >
          <ControlledTextArea
            control={control}
            name="processOfExperimentation"
            id={processOfExperimentationId}
            placeholder="Add your project's experimentation"
            rows={8}
          />
        </InputFormContainer>
        <ModalActions hasTopBorder={false}>
          <Button variant="white" rounded="regular" size="base" onClick={handleCancel}>
            Cancel
          </Button>
          <Button variant="primary" rounded="regular" size="base" type="submit">
            Save
          </Button>
        </ModalActions>
      </Stack>
    </ProjectEligibilityModal>
  );
};
